import SafeLocalStorage from './storage/SafeLocalStorage';

// WARNING: The value of SESSION_ID_STORAGE_KEY is relied upon by other Platform packages
// (eg. search-page, product-search-dialog). If you need to change this key, you MUST
// notify the dependant Platform teams and update all dependent packages accordingly.
const SESSION_ID_STORAGE_KEY = 'session.id';
const SESSION_EXPIRY_STORAGE_KEY = 'session.expiry';

const ONLY_NUMBERS_REGEX = /^\d+$/;

export const DEFAULT_SESSION_EXPIRY_TIME_MS: number = 30 * 60 * 1000; // 30 minutes
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const DEFAULT_EXPIRY_DEBOUNCE: number = 5 * 1000; // 5 seconds
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const DEFAULT_DEBOUNCE_THRESHOLD_PERCENTAGE = 0.9; // 90% of expiry
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const NOOP = (): void => {};

export type OptionsType = {
	onNewSessionStarted?: Function;
	sessionExpiryDebounce?: number;
	sessionExpiryDebounceThresholdPercentage?: number;
	sessionExpiryTime?: number;
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export default class SessionTracking {
	_safeLocalStorage: SafeLocalStorage;

	_sessionExpiryTime: number;

	_sessionExpiryDebounce: number;

	_sessionExpiryDebounceThresholdPercentage: number;

	_sessionExpiryDebounceTimer?: number;

	_onNewSessionStarted?: Function;

	constructor(options: OptionsType = {}) {
		this._sessionExpiryTime = options.sessionExpiryTime || DEFAULT_SESSION_EXPIRY_TIME_MS;
		this._sessionExpiryDebounce = options.sessionExpiryDebounce || DEFAULT_EXPIRY_DEBOUNCE;
		this._sessionExpiryDebounceThresholdPercentage =
			options.sessionExpiryDebounceThresholdPercentage || DEFAULT_DEBOUNCE_THRESHOLD_PERCENTAGE;

		this._onNewSessionStarted = options.onNewSessionStarted ? options.onNewSessionStarted : NOOP;

		this._safeLocalStorage = new SafeLocalStorage();
	}

	getCurrentSessionId(): string {
		const sessionId = this._safeLocalStorage.getItem(SESSION_ID_STORAGE_KEY);
		const sessionExpiry =
			parseInt(this._safeLocalStorage.getItem(SESSION_EXPIRY_STORAGE_KEY) || '0', 10) || 0;

		this._debouncedUpdateSessionExpiry(sessionExpiry);

		if (!sessionId || sessionExpiry <= Date.now() || Number.isNaN(sessionExpiry)) {
			return this._generateNewSessionId();
		}

		// Old versions of sessionId were UUIDs which caused issues for Amplitude which only accepts
		// integers for this field. In these scenarios we want to regenerate the sessionId as an integer
		if (!ONLY_NUMBERS_REGEX.test(sessionId)) {
			return this._generateNewSessionId();
		}

		return sessionId;
	}

	_generateNewSessionId = (): string => {
		const newSessionId = Date.now().toString();
		this._safeLocalStorage.setItem(SESSION_ID_STORAGE_KEY, newSessionId);

		// COMMENTS-123 - When a new session starts, call the callback if it is given
		this._onNewSessionStarted && this._onNewSessionStarted();
		return newSessionId;
	};

	_updateSessionExpiry = (): number => {
		const expiry = Date.now() + this._sessionExpiryTime;
		this._safeLocalStorage.setItem(SESSION_EXPIRY_STORAGE_KEY, expiry.toString());
		return expiry;
	};

	_debouncedUpdateSessionExpiry = (currentExpiry: number): void => {
		const { _updateSessionExpiry } = this;
		// If the current expiry is out of date by more than 10% of the expiry, update immediately
		if (
			currentExpiry <
			Date.now() + this._sessionExpiryDebounceThresholdPercentage * this._sessionExpiryTime
		) {
			_updateSessionExpiry();
		} else {
			if (this._sessionExpiryDebounceTimer) {
				clearTimeout(this._sessionExpiryDebounceTimer);
			}
			// @ts-ignore Something wrong with our types... this is legit
			this._sessionExpiryDebounceTimer = setTimeout(() => {
				_updateSessionExpiry();
			}, this._sessionExpiryDebounce);
		}
	};
}
